import React, { useState, useRef } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  useTheme,
  Alert,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Edit,
  Download,
  Add,
  ThreeDRotation,
} from '@mui/icons-material';

interface STLItem {
  id: string;
  name: string;
  filename: string;
  category: 'home' | 'gadgets' | 'art' | 'tools';
  description: string;
  fileSize: string;
  uploadedAt: string;
  downloadCount: number;
}

interface STLManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const STLManagement: React.FC<STLManagementProps> = ({ onNotification }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stlFiles, setStlFiles] = useState<STLItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingSTL, setEditingSTL] = useState<STLItem | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      // Upload each file to the backend
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('stl', file);
        formData.append('title', file.name.replace('.stl', '').replace('.zip', ''));
        formData.append('description', '');
        formData.append('category', 'home');

        const response = await fetch(API_ENDPOINTS.UPLOAD_STL, {
          method: 'POST',
          body: formData,
          credentials: 'include', // Include session cookie
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        return result.file;
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      const newSTLFiles: STLItem[] = uploadedFiles.map(file => ({
        id: file.id,
        name: file.title,
        filename: file.originalName,
        category: file.category,
        description: file.description,
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        uploadedAt: file.uploadedAt.split('T')[0],
        downloadCount: 0,
      }));

      setStlFiles(prev => [...newSTLFiles, ...prev]);
      onNotification(`${files.length} STL file(s) uploaded successfully!`, 'success');
    } catch (error) {
      onNotification('Failed to upload STL files', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteSTL = (id: string) => {
    setStlFiles(prev => prev.filter(file => file.id !== id));
    onNotification('STL file deleted successfully', 'success');
  };

  const handleEditSTL = (stl: STLItem) => {
    setEditingSTL(stl);
  };

  const handleSaveEdit = () => {
    if (!editingSTL) return;
    
    setStlFiles(prev => prev.map(file => 
      file.id === editingSTL.id ? editingSTL : file
    ));
    setEditingSTL(null);
    onNotification('STL file updated successfully', 'success');
  };

  const handleDownload = (stl: STLItem) => {
    // Simulate download
    onNotification(`Downloading ${stl.filename}...`, 'info');
    setStlFiles(prev => prev.map(file => 
      file.id === stl.id ? { ...file, downloadCount: file.downloadCount + 1 } : file
    ));
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'home': return 'primary';
      case 'gadgets': return 'secondary';
      case 'art': return 'success';
      case 'tools': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" color="primary" sx={{ fontWeight: 600 }}>
          STL Models Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={20} /> : 'Upload STL Files'}
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".stl,.zip"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        Upload STL files or ZIP archives containing multiple STL files. Maximum file size: 10MB per file.
      </Alert>

      <Grid container spacing={3}>
        {stlFiles.map((stl) => (
          <Grid item xs={12} sm={6} md={4} key={stl.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box
                sx={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.grey[100],
                  position: 'relative',
                }}
              >
                <ThreeDRotation sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Chip
                  label={stl.fileSize}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                  }}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {stl.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {stl.description || 'No description'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    label={stl.category}
                    color={getCategoryColor(stl.category) as any}
                    size="small"
                  />
                  <Chip
                    label={stl.uploadedAt}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stl.downloadCount} downloads
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton
                  size="small"
                  onClick={() => handleDownload(stl)}
                  color="primary"
                >
                  <Download />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleEditSTL(stl)}
                  color="secondary"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteSTL(stl.id)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {stlFiles.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No STL files uploaded yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Upload STL Files" to get started
          </Typography>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingSTL} onClose={() => setEditingSTL(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit STL File</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={editingSTL?.name || ''}
            onChange={(e) => setEditingSTL(prev => prev ? { ...prev, name: e.target.value } : null)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={editingSTL?.description || ''}
            onChange={(e) => setEditingSTL(prev => prev ? { ...prev, description: e.target.value } : null)}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            select
            label="Category"
            value={editingSTL?.category || 'home'}
            onChange={(e) => setEditingSTL(prev => prev ? { ...prev, category: e.target.value as any } : null)}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="home">Home Decor</option>
            <option value="gadgets">Gadgets</option>
            <option value="art">Art</option>
            <option value="tools">Tools</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSTL(null)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default STLManagement;
